import { css } from '@emotion/react';
import styled from '@emotion/styled';
import _ from 'lodash';

import itemListResponse from './helpers/item_list_response';
import joinPath from '../../../../helpers/joinPath';
import useReference from '../../../../hooks/use_reference';
import CheckboxSelector from '../../../ComponentLibrary/filter-list/oc-filters';
import TextSearchFilter from '../../../ComponentLibrary/filter-list/oc-text-search-filter';
import ColorFilter from '../../../ComponentLibrary/filter-tree/oc-color-filter';
import Tab from '../../../ComponentLibrary/oc-tab';
import TabList from '../../../ComponentLibrary/oc-tabs';
import Grid from '../../../OC/PageBuilder/Grid';
import MediaObject from '../../../OC/PageBuilder/MediaObject';
import Text from '../../../OC/PageBuilder/utilities/Text';
import Card from '../../../OC/oc-card-new';
import { H3 } from '../../../OC/oc-h';
import { useCloudinaryPictureOverlay } from '../../../OC/oc-picture-overlay';
import ColorPalette from '../../../Roofing/DesignAndInspire/ColorPalette';
import { imageOverlayConfig } from '../../../Roofing/DesignAndInspire/utils/imageOverlayConfig';
import { SortDropDown } from '../../../SDS/SortDropDown';
import { makeT } from '../../../location/locales';

const basePath = '/en-us/roofing/design-inspire';

const GroupSection = styled.section`
  margin-top: 0;
  margin-bottom: 32px;
  padding-bottom: 32px !important;
  &:last-of-type {
    border: none;
  }
`;

const CardLink = styled.a`
  text-decoration: none;

  &:hover {
    h2 {
      color: #d40f7d !important;
      text-decoration: underline;
    }
  }
`;

const withPairingItem = WrappedComponent => item => {
  const content = item?.contents?.[0];
  // console.log("withPairingItem", item);
  // Commenting the line below because of error: Rendered fewer hooks than expected. This may be caused by an accidental early return statement.
  // const { results, error, loading } = useReference(content.shingleColor?.color);
  const shingleColorData = content?.shingleColor?.color?.__data;
  const shingle = shingleColorData?.data;
  const shingleColor = shingle?.shingle_colors?.[0];

  return (
    <CardLink
      href={`/${item?.language_iso_code?.toLowerCase()}${item?.route}`}
      data-track="learn-more"
      data-track-destination={`${content?.colorName} Detail Page`}
      data-track-element-location="main section CTA"
    >
      <WrappedComponent
        {...content}
        shingle={shingle}
        shingleColor={shingleColor}
        shingleColorData={shingleColorData}
      />
    </CardLink>
  );
};

const StyleBoardViewComponent = withPairingItem(({ heroImage, colorName, shingleColor, shingleColorData }) => {
  const { data = {} } = shingleColorData || {};
  const { shingle_colors: shingleColors = [] } = data;
  const { baseLayer, overlays } = imageOverlayConfig({
    shingleColor: shingleColors.length > 0 ? { ...shingleColors[0] } : {},
    heroImage,
  });
  baseLayer.desktop.scaleSettings = { width: 1146, height: 612 };
  overlays.desktop[0].position = { x: -15, y: 100 };
  overlays.desktop[1] = {
    ...overlays.desktop[1],
    position: { x: 345, y: 45 },
    fontSize: shingleColor.name.length > 15 ? 26 : 32,
  };
  const { imageUrl } = useCloudinaryPictureOverlay({
    baseLayer: baseLayer.desktop,
    overlays: overlays.desktop,
  });

  return (
    <Card img={imageUrl} alt={heroImage?.desktop?.alt} transform="none">
      <H3 font="body" tag="h3">
        <Text content={`${colorName} & ${shingleColor?.name}`} />
      </H3>
    </Card>
  );
});

const ColorPaletteViewComponent = withPairingItem(({ colorName, colorPalette }) => (
  <ColorPalette heading={colorName} colorPalette={colorPalette} mode="horizontal" height="250px" />
));

const StyleBoardResultComponent = ({ ...props }) => <>{StyleBoardViewComponent(props.item)}</>;

const ColorPaletteResultComponent = ({ ...props }) => <>{ColorPaletteViewComponent(props.item)}</>;

const StyleBoardGrouping = ({ groupKey, children, title }) => {
  const t = makeT(Board.build.language);

  return (
    <GroupSection className="border">
      {groupKey.toString() === 'true' && (
        <h2 className="h3">
          <Text content={t(`shingles.featured_${title.replace(/\s/g, '')}`)} />
        </h2>
      )}
      <Grid
        selectedSize="3"
        selectedSizeTablet={groupKey.toString() === 'true' ? '3' : '2'}
        selectedSizeMobile={groupKey.toString() === 'true' ? '1' : '2'}
      >
        {children}
      </Grid>
    </GroupSection>
  );
};

const HeadingContainer = styled.div`
  align-items: center;
  display: inline-flex;
  position: relative;
  width: 100%;

  @media screen and (min-width: 600px) {
    width: auto;
  }

  // Temp solution for mobile styling tabs
  + .tabs-list {
    @media screen and (max-width: 600px) {
      margin-top: 0.5em;
    }
  }
`;

const WrappedH2 = styled.h2`
  margin-bottom: 0;
  margin-right: 0.5em;
`;

const WrappedTabs = styled(TabList)`
  margin-bottom: 0;
  margin-left: inherit !important;
  align-self: center !important;
  button {
    width: 185px;
  }
  button.active {
    border-bottom: 4px solid #d40f7d;
  }
`;

const ContentHeader = props => {
  const { count, activeTab, toggleTab, activeTitle, sortContent } = props;
  const SortComponent = sortContent.component;
  const t = makeT(Board.build.language);

  return (
    <>
      <HeadingContainer>
        <WrappedH2>
          <span>{count}</span> {activeTitle}
        </WrappedH2>
      </HeadingContainer>

      <WrappedTabs className="tabs-list">
        <Tab
          label={t('shingles.style_boards')}
          name="style-boards"
          active={activeTab === 'style-boards'}
          onClick={() => toggleTab('style-boards')}
        />
        <Tab
          label={t('shingles.color_palettes')}
          name="color-palettes"
          active={activeTab === 'color-palettes'}
          onClick={() => toggleTab('color-palettes')}
        />
      </WrappedTabs>

      {SortComponent ? <SortComponent styleProp={sortStyle} {...sortContent.props} /> : null}
    </>
  );
};

const sortStyle = css`
  label {
    font-weight: 700 !important;
  }
  @media only screen and (max-width: 1199px) {
    margin-top: 24px;
  }
`;

const contentHeaderStyle = css`
  padding-top: 0 !important;
`;

const getUniqArray = array => Array.from(new Set(array.map(JSON.stringify))).map(JSON.parse);

// used for sorting of filters
const compare = (a, b) =>
  Object.values(a)[1] > Object.values(b)[1] ? 1 : Object.values(b)[1] > Object.values(a)[1] ? -1 : 0;

const searchFields = ['name', 'accent_colors', 'main_house_colors', 'shingle_color_names', 'primary_shingle_colors'];

const colorPairingListResponse = pairingData => {
  const primaryShingleColors = [];
  const colorNames = [];
  const mainHouseColor = [];
  const accentColor = [];

  const t = makeT(Board.build.language);

  const getShingleColors = item => {
    const content = item?.contents?.[0];
    // Commenting the line below because of error: Rendered fewer hooks than expected. This may be caused by an accidental early return statement.
    // const { results, error, loading } = useReference(content.shingleColor?.color);
    const results = content?.shingleColor?.color?.__data;
    const shingle = results?.data;
    return shingle?.shingle_colors ?? [];
  };

  const getColors = item => {
    const colors = [];
    getShingleColors(item)?.forEach(shingleColor => shingleColor.colors.forEach(color => colors.push(color)));
    return colors;
  };

  const primaryColorDisplayName = str => t(`shingles.colors.${str}`);

  pairingData.forEach(pairing_item => {
    getColors(pairing_item).forEach(color =>
      primaryShingleColors.push({ value: color, displayName: primaryColorDisplayName(color) })
    );
    getShingleColors(pairing_item)?.forEach(shingleColor =>
      colorNames.push({ value: shingleColor.uid, displayName: shingleColor.name })
    );

    const filtering = pairing_item.metadata?.settings?.filtering;
    filtering?.mainHouseColor?.forEach(color =>
      mainHouseColor.push({ value: color, displayName: t(`shingles.color_filters.${color}`) })
    );
    filtering?.accentColor?.forEach(color =>
      accentColor.push({ value: color, displayName: t(`shingles.color_filters.${color}`) })
    );
  });
  const parseData = items =>
    items.map(item => ({
      primary_shingle_colors: getColors(item),
      shingle_color_names: getShingleColors(item)?.map(shingleColor => shingleColor.uid),
      main_house_colors: item.metadata?.settings?.filtering?.mainHouseColor,
      accent_colors: item.metadata?.settings?.filtering?.accentColor,
      name: `${item.name} & ${getShingleColors(item)?.map(shingleColor => shingleColor.name)[0]}`,
      item,
      featured: item?.contents?.[0]?.featured || false,
    }));

  const filters = {
    primary_shingle_colors: {
      component: ColorFilter,
      displayName: t('shingles.primary_shingle_color_blend'),
      options: getUniqArray(primaryShingleColors).sort(compare),
      valueType: 'multiple',
    },
    shingle_color_names: {
      component: CheckboxSelector,
      displayName: t('shingles.shingle_color_names'),
      options: getUniqArray(colorNames).sort(compare),
      valueType: 'multiple',
    },
    main_house_colors: {
      component: CheckboxSelector,
      displayName: t('shingles.main_house_color'),
      options: getUniqArray(mainHouseColor).sort(compare),
      valueType: 'multiple',
    },
    accent_colors: {
      component: CheckboxSelector,
      displayName: t('shingles.accent_color'),
      options: getUniqArray(accentColor).sort(compare),
      valueType: 'multiple',
    },
  };

  const sorts = {
    fields: {
      featured: {
        attribute: 'name',
        displayName: t('shingles.sort_by.featured'),
        direction: 'asc',
        default: true,
        group: true,
      },
      name_asc: {
        attribute: 'name',
        displayName: t('shingles.sort_by.alphabetical'),
        direction: 'asc',
      },
      name_desc: {
        attribute: 'name',
        displayName: t('shingles.sort_by.alphabetical_reverse'),
        direction: 'desc',
      },
    },
    component: SortDropDown,
    props: { label: t('shingles.sort_by.sort_by') },
  };

  const tabs = [
    {
      component: StyleBoardResultComponent,
      name: 'style-boards',
      title: t('shingles.style_boards'),
      displayStyle: 'grid',
      default: true,
      grouping: { key: 'featured', title: 'Style Board', component: StyleBoardGrouping },
    },
    {
      component: ColorPaletteResultComponent,
      name: 'color-palettes',
      title: t('shingles.color_palettes'),
      displayStyle: 'grid',
      grouping: { key: 'featured', title: 'Color', component: StyleBoardGrouping },
    },
  ];

  return itemListResponse({
    contentHeader: { component: ContentHeader, props: { headerStyle: contentHeaderStyle } },
    items: parseData(pairingData),
    filters,
    tabs,
    sorts,
    primaryActiveDefault: true,
    filterChipsVisible: false,
    Component: props => <MediaObject imgSize="large" {...props} />,
    searchFields,
    enableSearch: true,
  });
};

export default {
  ItemList: {
    availableIn: ['www.owenscorning.com'],
    name: 'Roofing - Design and Inspire - Color Pairings',
    meta: {},
    view: (data = null) => {
      const { data: items } = data || { meta: {} };
      return colorPairingListResponse((items || []).filter(x => x));
    },
  },
};
